import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Bed, Bath, ExternalLink, AlertTriangle } from 'lucide-react';
import clsx from 'clsx';
import LoadingSpinner from '../ui/LoadingSpinner';
import ScoreGauge from '../ui/ScoreGauge';
import YieldMeter from '../ui/YieldMeter';
import PriceBandBadge from '../ui/PriceBandBadge';
import PropertyImage from '../ui/PropertyImage';
import { pickPrimaryImage } from '../../utils/propertyImages';
import { formatCurrency, propertyTypeIcon } from '../../utils/formatters';

const VERDICT_STYLE = {
  STRONG_BUY: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40',
  BUY:        'bg-teal-500/20 text-teal-300 border-teal-500/40',
  HOLD:       'bg-amber-500/20 text-amber-300 border-amber-500/40',
  AVOID:      'bg-red-500/20 text-red-300 border-red-500/40',
};

export default function PropertyAddressFocus({ propertyId, matchCount, viewerAccess }) {
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!propertyId) return;
    setLoading(true);
    setError(null);
    const token = localStorage.getItem('assetlens_token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    fetch(`/api/properties/${propertyId}`, { headers })
      .then(r => {
        if (!r.ok) throw new Error('Could not load property details');
        return r.json();
      })
      .then(setProperty)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [propertyId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10 bg-slate-900 border border-slate-800 rounded-2xl">
        <LoadingSpinner size={32} className="text-emerald-500" />
      </div>
    );
  }

  if (error || !property) {
    return (
      <div className="flex items-center gap-3 p-5 bg-amber-500/10 border border-amber-500/20 rounded-2xl text-amber-200 text-sm">
        <AlertTriangle size={18} />
        {error || 'Property details unavailable'}
      </div>
    );
  }

  const score = property.score;
  const verdict = property.ai_insight?.verdict;
  const gated = viewerAccess === 'browsing' || !score;

  return (
    <section className="bg-slate-900 border border-emerald-500/30 rounded-2xl overflow-hidden">
      <div className="px-5 py-3 border-b border-slate-800 bg-emerald-500/5 flex items-center justify-between gap-3 flex-wrap">
        <p className="text-emerald-300 text-sm font-medium">
          Address match{matchCount > 1 ? ` — best of ${matchCount.toLocaleString()} results` : ''}
        </p>
        <Link
          to={`/properties/${property.id}`}
          className="inline-flex items-center gap-1.5 text-xs text-emerald-400 hover:text-emerald-300"
        >
          Full property page <ExternalLink size={12} />
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_240px] gap-0">
        <div className="flex flex-col sm:flex-row gap-0">
          <PropertyImage
            src={pickPrimaryImage(property)}
            alt={property.address}
            wrapperClassName="sm:w-56 h-48 sm:h-auto sm:min-h-[200px] shrink-0 overflow-hidden bg-slate-800"
          />

          <div className="p-5 space-y-4 flex-1 min-w-0">
            <div>
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <span className="text-xl">{propertyTypeIcon(property.property_type)}</span>
                {score?.price_band && <PriceBandBadge band={score.price_band} size="sm" />}
                {verdict && (
                  <span className={clsx('text-[10px] font-semibold px-1.5 py-0.5 rounded border', VERDICT_STYLE[verdict])}>
                    AI: {verdict.replace('_', ' ')}
                  </span>
                )}
                {property.status === 'stc' && (
                  <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded border bg-amber-500/20 text-amber-300 border-amber-500/40">
                    STC
                  </span>
                )}
              </div>
              <h2 className="text-xl font-bold text-white leading-snug">{property.address}</h2>
              <div className="flex items-center gap-2 text-slate-400 text-sm mt-1 flex-wrap">
                <MapPin size={14} className="shrink-0" />
                <span>{property.postcode}</span>
                {property.town && <><span>·</span><span>{property.town}</span></>}
              </div>
            </div>

            <div className="flex items-center gap-6 flex-wrap text-sm">
              {property.asking_price != null && (
                <div>
                  <p className="text-slate-500 text-xs">Asking price</p>
                  <p className="text-white font-semibold text-lg">{formatCurrency(property.asking_price)}</p>
                </div>
              )}
              {score?.estimated_value != null && (
                <div>
                  <p className="text-slate-500 text-xs">Est. value</p>
                  <p className="text-emerald-400 font-semibold text-lg">{formatCurrency(score.estimated_value)}</p>
                </div>
              )}
              {property.bedrooms != null && (
                <div className="flex items-center gap-1.5 text-slate-300">
                  <Bed size={15} /> {property.bedrooms} bed
                </div>
              )}
              {property.bathrooms != null && (
                <div className="flex items-center gap-1.5 text-slate-300">
                  <Bath size={15} /> {property.bathrooms} bath
                </div>
              )}
            </div>

            {property.description && (
              <p className="text-slate-400 text-sm line-clamp-3">{property.description}</p>
            )}

            {property.ai_insight?.summary && (
              <div className="bg-slate-800/60 border border-slate-700 rounded-xl p-4">
                <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">AI summary</p>
                <p className="text-slate-300 text-sm">{property.ai_insight.summary}</p>
              </div>
            )}

            {property.sales_history?.length > 0 && (
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide mb-2">Recent sales nearby</p>
                <ul className="space-y-2 text-sm text-slate-400">
                  {(() => {
                    const seenNotes = new Set();
                    return property.sales_history.slice(0, 3).map(s => {
                      const showNote = s.price_note && !seenNotes.has(s.price_note);
                      if (showNote) seenNotes.add(s.price_note);
                      return (
                        <li key={s.id}>
                          <div className="flex justify-between gap-4">
                            <span className="truncate">{s.address || s.postcode}</span>
                            <span className="text-slate-300 shrink-0">
                              {s.sale_price ? formatCurrency(s.sale_price) : '—'}
                              {s.sale_date && (
                                <span className="text-slate-500 ml-2">
                                  {new Date(s.sale_date).getFullYear()}
                                </span>
                              )}
                            </span>
                          </div>
                          {showNote && (
                            <p className="mt-1 text-xs text-amber-200/80 leading-relaxed">{s.price_note}</p>
                          )}
                        </li>
                      );
                    });
                  })()}
                </ul>
              </div>
            )}
          </div>
        </div>

        <div className="p-5 border-t lg:border-t-0 lg:border-l border-slate-800 flex items-center justify-center">
          {gated ? (
            <div className="text-center py-4 px-3 border border-dashed border-slate-700 rounded-xl w-full">
              <p className="text-slate-300 text-sm mb-2">Scores and analysis require Basic or Pro.</p>
              <Link
                to="/choose-plan"
                className="inline-block bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold px-4 py-2 rounded-xl"
              >
                View plans
              </Link>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-4">
              <ScoreGauge score={score?.investment_score} size={100} label="Score" />
              <YieldMeter yieldPct={score?.gross_yield_pct} size={90} label="Yield" />
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
